import { useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { List, ListStatus } from 'dashboard/models/list';
import { ProductFeatureName } from 'dashboard/models/organization';
import { User } from 'dashboard/models/user';
import { WorkflowFieldObjectKey } from 'dashboard/models/workflow';
import { setList, setListsFilters } from 'dashboard/redux/actions/lists';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { loadLists } from 'dashboard/redux/thunk-actions/list';
import { loadWorkflowSchemaForObject } from 'dashboard/redux/thunk-actions/workflow-schema';
import DashboardListsView from 'dashboard/views/DashboardListsView';
import { AddListData } from 'dashboard/views/DashboardListsView/AddListModal';
import ApiResponseError from 'lib/errors/ApiResponseError';
import useApiLoader from 'lib/hooks/useApiLoader';
import usePermission from 'lib/hooks/usePermission';
import useProductFeature from 'lib/hooks/useProductFeature';
import { Permission } from 'lib/permissions';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

const DashboardListsRoute = ({
  currentEnvironment,
  inquiryTemplateSchemas,
  lists,
  listsSortParams,
  listsFilterParams,
  organization,
  users,
  loadLists,
  loadWorkflowSchemaForObject,
  setList,
  setListsFilters,
  showMessage,
}: PropsFromRedux) => {
  const featureIsDisabled = !useProductFeature(ProductFeatureName.ListsDashboard);
  const hasListsBasic = useProductFeature(ProductFeatureName.ListsBasic);
  const hasListsEnhanced = useProductFeature(ProductFeatureName.ListsEnhanced);
  const hasViewPermission = usePermission(Permission.ListView);

  const abortController = useMemo(
    () => new AbortController(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadLists, currentEnvironment, listsFilterParams, listsSortParams],
  );

  const { loading: isLoadingLists } = useApiLoader({
    loader: () =>
      loadLists({
        currentEnvironment,
        filters: listsFilterParams || {},
        sortParams: listsSortParams || { sort: 'created_at', shouldSortDescending: true },
        abortSignal: abortController.signal,
      }),
    deps: [loadLists, currentEnvironment, listsSortParams, listsFilterParams],
    skip: featureIsDisabled,
    onError: (error) => {
      if (isPermissionsError(error)) {
        showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        showMessage('Failed to load lists.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
  });

  const { loading: isLoadingSchema } = useApiLoader({
    loader: () => loadWorkflowSchemaForObject(WorkflowFieldObjectKey.Inquiry),
    deps: [loadWorkflowSchemaForObject],
    onError: (error) => {
      if (isPermissionsError(error)) {
        showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        showMessage('Failed to load Inquiry schema.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
  });

  const addList = async (values: AddListData): Promise<void> => {
    try {
      const response = await SessionClientDashboard.postResourceCreate<List>({
        attributes: {
          'environment-id': currentEnvironment.id,
          name: values.name,
          'allow-fuzzy-name-first': values.allowFuzzy || null,
          'field-name': values.fieldName || null,
          'items-expire-after-days': values.itemsExpireAfterDays || null,
          'retention-period-seconds': values.retentionPeriodSeconds || null,
        },
        type: values.type,
      });

      const list = response.data;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const user = response.included[0] as User;

      if (hasViewPermission) {
        setList(list, user);
      }

      showMessage(`Successfully created ${values.name} list.`, MessageType.Success);
    } catch (err) {
      // NOTE: we should be deliberate about checking error codes. See
      // https://www.notion.so/withpersona/Error-serialization-282f3c016ee7450eabe42dd273762cf0
      if (err instanceof ApiResponseError && err.status === 409) {
        showMessage('There is already an active list with this name.', MessageType.Failure);
      } else {
        showMessage('Please try again.', MessageType.Failure);
      }
    }
  };

  const handleStatusFilterUpdate = (statuses: ListStatus[]) => {
    setListsFilters({
      status: statuses,
    });
  };

  return (
    <DashboardListsView
      featureIsDisabled={featureIsDisabled}
      hasListsBasic={hasListsBasic}
      hasListsEnhanced={hasListsEnhanced}
      currentEnvironment={currentEnvironment}
      organization={organization}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      inquiryTemplateSchemas={inquiryTemplateSchemas}
      lists={lists}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      filters={listsFilterParams}
      isLoading={isLoadingLists || isLoadingSchema}
      users={users}
      handleSubmitAddList={addList}
      handleStatusFilterUpdate={handleStatusFilterUpdate}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    inquiryTemplateSchemas:
      state.workflowSchema['criteria-variable-schemas']?.inquiry?.['with-template'],
    lists: state.lists.lists,
    listsFilterParams: state.lists.filters,
    listsSortParams: state.lists.sortParams,
    organization: state.organization.organization,
    users: state.lists.users,
  };
};

const mapDispatchToProps = {
  loadLists,
  loadWorkflowSchemaForObject,
  setList,
  setListsFilters,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardListsRoute);
